import { useEffect, useRef, useState } from "react";
import { PlatformApiError, type PlatformApiClient } from "../../api";

type Channel = "email" | "whatsapp";
interface Schedule {
  scheduleId: string;
  campaignId: string;
  channel: Channel;
  state: "scheduled" | "cancelled";
  scheduledFor: string;
  timezone: string;
  recipientCap: number;
  maxReminders: number;
  sendAuthorized: false;
  providerCalls: 0;
  replayed?: boolean;
}
interface ListResponse {
  schedules: Schedule[];
  sendAuthorized: false;
}
function valid(item: Schedule): boolean {
  return !!item && /^rac_schedule_[a-f0-9-]{36}$/.test(item.scheduleId)
    && (item.channel === "email" || item.channel === "whatsapp")
    && (item.state === "scheduled" || item.state === "cancelled")
    && Number.isSafeInteger(item.recipientCap) && item.recipientCap >= 1
    && item.recipientCap <= 100 && [0, 1].includes(item.maxReminders)
    && !Number.isNaN(Date.parse(item.scheduledFor))
    && item.sendAuthorized === false && item.providerCalls === 0;
}

export function CampaignSchedulePanel({
  slug, api, campaignId, channel, onExpired,
}: {
  slug: string;
  api: PlatformApiClient;
  campaignId: string;
  channel: Channel;
  onExpired: () => void;
}) {
  const [available, setAvailable] = useState<"loading" | "ready" | "disabled" | "error">("loading");
  const [schedule, setSchedule] = useState<Schedule | null>(null);
  const [dateTime, setDateTime] = useState("");
  const [recipientCap, setRecipientCap] = useState(50);
  const [maxReminders, setMaxReminders] = useState(0);
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const requestKey = useRef<string | null>(null);
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";

  useEffect(() => {
    let active = true;
    setAvailable("loading");
    setSchedule(null);
    setMessage("");
    requestKey.current = null;
    void api.reputation<ListResponse>(slug, "/acquisition/schedules")
      .then(data => {
        if (!active) return;
        if (data.sendAuthorized !== false || !Array.isArray(data.schedules)
          || data.schedules.length > 20 || !data.schedules.every(valid)) {
          setAvailable("error");
          return;
        }
        setSchedule(data.schedules.find(item =>
          item.campaignId === campaignId && item.channel === channel) ?? null);
        setAvailable("ready");
      })
      .catch(cause => {
        if (!active) return;
        if (cause instanceof PlatformApiError && cause.status === 401) {
          onExpired();
          return;
        }
        setAvailable(cause instanceof PlatformApiError
          && [403, 404].includes(cause.status) ? "disabled" : "error");
      });
    return () => { active = false; };
  }, [slug, api, campaignId, channel, onExpired]);

  async function createSchedule() {
    const date = new Date(dateTime);
    const timestamp = date.getTime();
    if (!confirm || busy || available !== "ready" || schedule
      || !Number.isFinite(timestamp)
      || timestamp < Date.now() + 5 * 60 * 1000
      || timestamp > Date.now() + 365 * 24 * 60 * 60 * 1000) {
      setMessage("Elegí una fecha válida entre cinco minutos y un año desde ahora.");
      return;
    }
    setBusy(true);
    setMessage("");
    requestKey.current ??= "campaign-schedule-web-" + globalThis.crypto.randomUUID();
    try {
      const result = await api.reputation<Schedule>(slug, "/acquisition/schedules", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          requestKey: requestKey.current, campaignId, channel,
          scheduledFor: date.toISOString(), timezone, recipientCap, maxReminders,
        }),
      });
      if (!valid(result) || result.campaignId !== campaignId || result.channel !== channel
        || result.state !== "scheduled") throw new Error("schedule_contract_invalid");
      setSchedule(result);
      setConfirm(false);
      requestKey.current = null;
      setMessage("Programación guardada. Todavía no se autorizó ningún envío.");
    } catch (cause) {
      if (cause instanceof PlatformApiError && cause.status === 401) onExpired();
      else setMessage(cause instanceof PlatformApiError && cause.status === 409
        ? "Conflicto con una programación existente. Actualizá el estado."
        : "No se pudo verificar la programación. No vuelvas a enviarla sin revisar el estado.");
    } finally {
      setBusy(false);
    }
  }
  async function cancelSchedule() {
    if (!schedule || schedule.state !== "scheduled" || busy) return;
    setBusy(true);
    setMessage("");
    try {
      const response = await api.reputation<Schedule>(
        slug, "/acquisition/schedules/" + encodeURIComponent(schedule.scheduleId) + "/cancel", {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: "{}",
        });
      if (!valid(response) || response.scheduleId !== schedule.scheduleId
        || response.state !== "cancelled") throw new Error("schedule_contract_invalid");
      setSchedule(response);
      setMessage("Programación cancelada. No se autoriza ningún despacho.");
    } catch (cause) {
      if (cause instanceof PlatformApiError && cause.status === 401) onExpired();
      else setMessage("No se pudo confirmar la cancelación. Consultá al administrador antes de continuar.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="rep-acquisition-schedule" aria-label="Programación de campaña">
      <h3>Programación de campaña</h3>
      {available === "loading" && <p role="status">Consultando programación…</p>}
      {available === "disabled" && <p>La programación todavía no está habilitada para esta organización.</p>}
      {available === "error" && <p role="alert">No se pudo consultar la programación.</p>}
      {available === "ready" && schedule !== null && <>
        <p><strong>{schedule.state === "scheduled" ? "Programada" : "Cancelada"}</strong></p>
        <p>{new Date(schedule.scheduledFor).toLocaleString()} · {schedule.timezone}</p>
        <p>Máximo: {schedule.recipientCap} destinatarios. Recordatorios: {schedule.maxReminders}.</p>
        {schedule.state === "scheduled" &&
          <button type="button" disabled={busy} onClick={() => { void cancelSchedule(); }}>
            {busy ? "Cancelando…" : "Cancelar programación"}
          </button>}
      </>}
      {available === "ready" && schedule === null && <>
        <p>Programá el inicio de una campaña aprobada. El envío requiere además consentimiento y validación de destinatarios.</p>
        <label htmlFor="rep-schedule-at">Fecha y hora local</label>
        <input id="rep-schedule-at" type="datetime-local" value={dateTime}
          disabled={busy} onChange={event => setDateTime(event.target.value)} />
        <p>Zona horaria del navegador: {timezone}</p>
        <label htmlFor="rep-schedule-cap">Límite de destinatarios (1–100)</label>
        <input id="rep-schedule-cap" type="number" min={1} max={100} step={1}
          value={recipientCap} disabled={busy}
          onChange={event => setRecipientCap(Number(event.target.value))} />
        <label htmlFor="rep-schedule-reminders">Recordatorio máximo</label>
        <select id="rep-schedule-reminders" value={maxReminders} disabled={busy}
          onChange={event => setMaxReminders(Number(event.target.value))}>
          <option value={0}>Ninguno</option>
          <option value={1}>Uno</option>
        </select>
        <label className="rep-acquisition-check">
          <input type="checkbox" checked={confirm} disabled={busy}
            onChange={event => setConfirm(event.target.checked)} />
          Confirmo la fecha y los límites. Sé que programar no envía mensajes.
        </label>
        <button type="button" disabled={!confirm || busy || !dateTime
          || !Number.isSafeInteger(recipientCap) || recipientCap < 1 || recipientCap > 100}
          onClick={() => { void createSchedule(); }}>
          {busy ? "Guardando…" : "Guardar programación"}
        </button>
      </>}
      {message && <p role="status" aria-live="polite">{message}</p>}
    </section>
  );
}
